import { useState, useMemo, type FC } from "react";
import { Divider, Text, getAvatarLetters } from "@certa/blocks/thanos";
import type {
  CommentChannel,
  CommentMention,
  ConversationThread
} from "@certa/types";

import {
  AvatarColors,
  AvatarSizes,
  Avatar
} from "@certa/catalyst/components/Avatar";
import {
  TypographyColors,
  TypographyVariants,
  Typography
} from "@certa/catalyst/components/Typography";
import { Tooltip } from "@certa/catalyst/components/Tooltip";

import { Stack } from "@certa/catalyst/layouts/Stack";
import { HierarchyBreadcrumbs } from "./HierarchyBreadcrumbs";
import { ChannelContext, useChannelContext } from "../../comments.context";
import moment from "moment";
import { useCommentMessageFormat } from "../../hooks/useCommentMessageFormat";
import { prepareMentionOptions } from "../../comments.utils";
import { MultiChannelWithTabs } from "./MultiChannelWithTabs";
import { css } from "emotion";
import { messageStyles } from "./CommentsRedesigned.styles";

import { useScreenResolution } from "@certa/common/hooks/useScreenResolution";
import { ABSOLUTE_DATE_TIME_FORMAT } from "@certa/common/utils/time";
import { useCommsNewEndpoints } from "@certa/common/toggles";
import { ConversationListItemAppRedesign } from "./ConversationListItemAppRedesign";
import type { InfiniteConversationCommsModel } from "./ConversationListItemAppRedesign";
import { CatalystColors } from "@certa/catalyst/constants/styles";
import { Messages } from "@certa/icons/components/Messages";
import { Badge } from "antd";
import { ChevronRight } from "@certa/icons/components/ChevronRight";
import {
  HorizontalTab,
  HorizontalTabs
} from "@certa/catalyst/components/HorizontalTab";
import {
  useGetMessagesCount,
  type MessagesCount
} from "@certa/queries/queries/comments/getMessagesCount.query";
import { NoComments } from "./NoComments";
import { useIntl } from "react-intl";
import { AnalyticsEvents, analyticsService } from "@certa/analytics";
import { useAuth } from "@certa/auth/AuthenticationProvider";

const ChannelListItemAppRedesign: FC<{
  handleChannelSelect: (
    channel: CommentChannel,
    threadWithLatestMessage: number
  ) => void;
  stepCounts?: Record<string, MessagesCount>;
  fieldCounts?: Record<string, MessagesCount>;
}> = ({ handleChannelSelect, stepCounts, fieldCounts }) => {
  const channel = useChannelContext();

  const unreadCount =
    channel.objectType === "field"
      ? fieldCounts?.[channel.objectId].unread
      : stepCounts?.[channel.objectId].unread;

  /**
   * Get all the latest messages from all the threads of the current channel
   * The latest message from every thread will be last array element
   *
   * Also extracting complete thread data here to use it for getting mentions
   */
  const latestMessageListFromAllThreads = channel.allThreads
    .filter(thread => thread.messageCount > 0)
    .map(thread => ({
      thread: thread,
      message:
        channel.threads[thread.threadId].messages[
          channel.threads[thread.threadId].messages.length - 1
        ]
    }));

  /**
   * Compare the dates of every latest message of all the threads
   * and get the greatest date, that will be out latest message
   */
  const latestMessage = latestMessageListFromAllThreads.reduce((a, b) =>
    new Date(a.message.createdAt) > new Date(b.message.createdAt) ? a : b
  );

  const mentions: CommentMention[] = prepareMentionOptions({
    threadUserGroups: latestMessage.thread.threadUserGroups || [],
    threadUsers: latestMessage.thread.threadUsers || []
  });

  const formattedMessage = useCommentMessageFormat(
    latestMessage.message.message,
    mentions,
    true
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleChannelSelect(channel, latestMessage.thread.threadId);
    }
    if (event.key === " ") {
      event.preventDefault();
      handleChannelSelect(channel, latestMessage.thread.threadId);
    }
  };

  return (
    <Stack
      direction="vertical"
      padding="16"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      role="button"
      onClick={() =>
        handleChannelSelect(channel, latestMessage.thread.threadId)
      }
      dangerouslySetClassName={css({
        cursor: "pointer",
        border: `1px solid ${CatalystColors.NEUTRAL_400}`,
        borderRadius: "0.75rem",
        "&:hover": {
          backgroundColor: CatalystColors.TEAL_100
        }
      })}
    >
      <Stack direction="horizontal" width="100%" justify="space-between">
        <HierarchyBreadcrumbs containerWidth="100%" isReadOnly={true} />
        <Stack direction="horizontal" justify="end" gap="8" padding="4">
          <Badge dot count={unreadCount ?? 0} offset={[-2, 2]}>
            <Messages />
          </Badge>
          <ChevronRight color={CatalystColors.BRAND_500} />
        </Stack>
      </Stack>
      <Divider />
      <Stack direction="vertical" gap="16" align="start">
        <Stack
          direction="horizontal"
          justify="space-between"
          width="100%"
          gap="12"
        >
          <Stack
            direction="horizontal"
            justify="start"
            gap="8"
            dangerouslySetClassName={css({
              minWidth: 0
            })}
          >
            <Avatar
              size={AvatarSizes.BIG}
              color={AvatarColors.BRAND}
              aria-label={latestMessage.message.postedBy}
            >
              {getAvatarLetters(latestMessage.message.postedBy)}
            </Avatar>
            <Stack
              direction="vertical"
              gap="4"
              dangerouslySetClassName={css({
                minWidth: 0
              })}
            >
              <Stack direction="horizontal" gap="8">
                <Typography
                  variant={TypographyVariants.LABEL_SM_BOLD}
                  color={TypographyColors.NEUTRAL_800}
                >
                  {latestMessage.message.postedBy}
                </Typography>

                <Stack
                  direction="horizontal"
                  align="start"
                  justify="end"
                  gap="8"
                  dangerouslySetClassName={css({
                    flexShrink: 0,
                    minWidth: 0
                  })}
                >
                  <Tooltip
                    content={moment(latestMessage.message.createdAt).format(
                      ABSOLUTE_DATE_TIME_FORMAT
                    )}
                  >
                    <Typography
                      variant={TypographyVariants.LABEL_SM}
                      color={TypographyColors.NEUTRAL_800}
                    >
                      {moment(latestMessage.message.createdAt)
                        .startOf("minutes")
                        .fromNow()}
                    </Typography>
                  </Tooltip>
                </Stack>
              </Stack>

              <Text
                variant="p1-regular"
                lineClamp={3}
                className={messageStyles}
                dangerouslySetInnerHTML={{
                  __html: formattedMessage
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export type AllChannelsProps = {
  channels: CommentChannel[];
  threadsComms: InfiniteConversationCommsModel;
  handleChannelSelect: (
    channel: CommentChannel,
    threadWithLatestMessage: number
  ) => void;
  handleChannelSelectComms: (conversationThread: ConversationThread) => void;
  multiChannelWithTabs?: boolean;
};

const MultiChannelViewAppRedesign: FC<AllChannelsProps> = ({
  channels,
  threadsComms,
  handleChannelSelect,
  handleChannelSelectComms,
  multiChannelWithTabs
}) => {
  const intl = useIntl();

  /**
   * We only require only those channels which have at least
   * one message sent under any of it's thread
   */
  const filteredChannels = channels.filter(channel => {
    const totalMessagesInChannel = channel.allThreads.reduce(
      (total, thread) => total + thread.messageCount,
      0
    );
    return totalMessagesInChannel > 0;
  });

  const { isMobileResolution } = useScreenResolution();
  const isCommsEnabled = useCommsNewEndpoints();
  const { userId } = useAuth();

  const [activeTab, setActiveTab] = useState("all");

  const allStepIds = channels
    .filter(channel => channel.objectType === "step")
    .map(channel => `${channel.objectId}`);
  const allFieldIds = channels
    .filter(channel => channel.objectType === "field")
    .map(channel => `${channel.objectId}`);

  const { data: stepMessagesCountData } = useGetMessagesCount({
    objectIds: allStepIds,
    contentType: "step"
  });

  const { data: fieldMessagesCountData } = useGetMessagesCount({
    objectIds: allFieldIds,
    contentType: "field"
  });

  const unreadChannels = filteredChannels.filter(channel => {
    const unreadCount =
      (channel.objectType === "field"
        ? fieldMessagesCountData?.counts?.[channel.objectId].unread
        : stepMessagesCountData?.counts?.[channel.objectId].unread) ?? 0;
    return unreadCount > 0;
  });

  const isEmpty =
    activeTab === "all"
      ? filteredChannels.length === 0
      : unreadChannels.length === 0;

  const noCommentsMessage = intl.formatMessage({
    id: "comments.noCommentsInThisView",
    defaultMessage: "There are no comments in this view."
  });

  const filteredThreadsComms = useMemo(() => {
    if (activeTab === "all") {
      return threadsComms;
    }

    if (!threadsComms?.data) {
      return threadsComms;
    }

    const filteredPages = threadsComms.data.pages.map(page => ({
      ...page,
      results: page.results.filter(thread => {
        const latestMessage = thread.latestMessage;
        return !latestMessage.readBy?.includes(userId);
      })
    }));

    return {
      ...threadsComms,
      data: {
        ...threadsComms.data,
        pages: filteredPages
      }
    };
  }, [threadsComms, activeTab, userId]);

  const isEmptyComms = useMemo(() => {
    if (!filteredThreadsComms?.data?.pages) {
      return true;
    }
    return filteredThreadsComms.data.pages.every(
      page => page.results.length === 0
    );
  }, [filteredThreadsComms]);

  const isViewEmpty = isCommsEnabled ? isEmptyComms : isEmpty;

  return (
    <Stack
      gap="16"
      direction="vertical"
      height="100%"
      dangerouslySetClassName={css({
        height: multiChannelWithTabs ? "100%" : "auto"
      })}
    >
      <Stack padding={isMobileResolution ? "16 8 0 8" : "16 24 0 24"}>
        <HorizontalTabs
          aria-label="Comment channels"
          onChange={tab => {
            if (tab === "unread") {
              analyticsService.track(
                AnalyticsEvents.workflowDetailEvents
                  .COMMENT_OVERVIEW_PANE_CLICK_UNREAD_TAB
              );
            } else {
              analyticsService.track(
                AnalyticsEvents.workflowDetailEvents
                  .COMMENT_OVERVIEW_PANE_CLICK_READ_TAB
              );
            }

            setActiveTab(tab);
          }}
          activeTab={activeTab}
        >
          <HorizontalTab aria-label="All Comments" value="all">
            All Comments
          </HorizontalTab>
          <HorizontalTab aria-label="Unread" value="unread">
            Unread
          </HorizontalTab>
        </HorizontalTabs>
      </Stack>
      <Stack
        height="100%"
        overflowY="auto"
        padding={isMobileResolution ? "0 8 48 8" : "0 24 48 24"}
      >
        {isViewEmpty ? (
          <NoComments text={noCommentsMessage} isMultiChannel />
        ) : isCommsEnabled ? (
          <ConversationListItemAppRedesign
            threadsComms={filteredThreadsComms}
            handleChannelSelectComms={handleChannelSelectComms}
            stepCounts={stepMessagesCountData?.counts}
            fieldCounts={fieldMessagesCountData?.counts}
          />
        ) : (
          <Stack gap="8">
            {multiChannelWithTabs ? (
              <MultiChannelWithTabs
                filteredChannels={filteredChannels}
                handleChannelSelect={handleChannelSelect}
              />
            ) : (
              (activeTab === "all" ? filteredChannels : unreadChannels).map(
                channel => (
                  <ChannelContext.Provider
                    value={channel}
                    key={`${channel.objectId}`}
                  >
                    <ChannelListItemAppRedesign
                      stepCounts={stepMessagesCountData?.counts}
                      fieldCounts={fieldMessagesCountData?.counts}
                      handleChannelSelect={handleChannelSelect}
                    />
                  </ChannelContext.Provider>
                )
              )
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export { MultiChannelViewAppRedesign };
